// File: utils/dashboard/useProgressTracker.js

import { useState, useEffect } from 'react';
import { getDefaultCardByUserId } from 'apis/cardApi';
import { getContactLinksByCard } from 'apis/contactLinkApi';
import { getCardsByUser } from 'apis/userApi';
import { getLeadsByUser } from 'apis/leadCaptureApi';

const useProgressTracker = () => {
  // Initialize states from localStorage if available
  const [createCard, setCreateCard] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('createCard') || 'false');
    } catch (error) {
      console.error('Error parsing createCard:', error);
      return false;
    }
  });

  const [addTwoLinks, setAddTwoLinks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('addTwoLinks') || 'false');
    } catch (error) {
      console.error('Error parsing addTwoLinks:', error);
      return false;
    }
  });

  const [addProfilePhoto, setAddProfilePhoto] = useState(() => {
    try {
      const storedValue = localStorage.getItem('addProfilePhoto');
      return storedValue ? JSON.parse(storedValue) : false;
    } catch (error) {
      console.error('Error parsing addProfilePhoto:', error);
      return false;
    }
  });

  const [shareCard, setShareCard] = useState(() => {
    try {
      const storedValue = localStorage.getItem('shareCard');
      return storedValue ? JSON.parse(storedValue) : false;
    } catch (error) {
      console.error('Error parsing shareCard:', error);
      return false;
    }
  });

  const [captureLead, setCaptureLead] = useState(() => {
    try {
      const storedValue = localStorage.getItem('captureLead');
      return storedValue ? JSON.parse(storedValue) : false;
    } catch (error) {
      console.error('Error parsing captureLead:', error);
      return false;
    }
  });

  const [completedSteps, setCompletedSteps] = useState(0);

  // Save states to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('createCard', JSON.stringify(createCard));
      localStorage.setItem('addTwoLinks', JSON.stringify(addTwoLinks));
      localStorage.setItem('addProfilePhoto', JSON.stringify(addProfilePhoto));
      localStorage.setItem('shareCard', JSON.stringify(shareCard));
      localStorage.setItem('captureLead', JSON.stringify(captureLead));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [createCard, addTwoLinks, addProfilePhoto, shareCard, captureLead]);

  // Method to count completed steps
  const updateCompletedSteps = useEffect(() => {
    let steps = 0;

    // Step 1: Card Creation
    if (createCard) steps++;

    // Step 2: Profile Photo AND Two Links (counts as one step)
    if (addProfilePhoto && addTwoLinks) steps++;

    // Step 3: Share Card
    if (shareCard) steps++;

    // Step 4: Capture Lead
    if (captureLead) steps++;

    setCompletedSteps(steps);
  }, [createCard, addTwoLinks, addProfilePhoto, shareCard, captureLead]);

  // Method to check if user has created a card
  const checkCardCreation = async (userId) => {
    try {
      // Check if we're in signup flow - if so, skip this check
      const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
      if (isSignupFlowActive) {
        console.log('🔍 useProgressTracker: Skipping checkCardCreation during signup flow');
        return false;
      }

      const userCards = await getCardsByUser(userId);
      const hasCards = userCards && userCards.length > 0;
      setCreateCard(hasCards);
      return hasCards;
    } catch (error) {
      console.error('Error checking card creation status:', error);
      return false;
    }
  };

  // Method to check if default card has a profile photo
  const checkProfilePhoto = async (userId) => {
    try {
      // Check if we're in signup flow - if so, skip this check
      const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
      if (isSignupFlowActive) {
        console.log('🔍 useProgressTracker: Skipping checkProfilePhoto during signup flow');
        return false;
      }

      const defaultCard = await getDefaultCardByUserId(userId);
      if (defaultCard) {
        const hasProfilePhoto = defaultCard.profilePictureUrl && defaultCard.profilePictureUrl.trim() !== '';
        setAddProfilePhoto(hasProfilePhoto);
        return hasProfilePhoto;
      }
      return false;
    } catch (error) {
      console.error('Error checking profile photo status:', error);
      return false;
    }
  };

  // Method to check if default card has at least two contact links
  const checkTwoLinks = async (userId) => {
    try {
      // Check if we're in signup flow - if so, skip this check
      const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
      if (isSignupFlowActive) {
        console.log('🔍 useProgressTracker: Skipping checkTwoLinks during signup flow');
        return false;
      }

      const defaultCard = await getDefaultCardByUserId(userId);
      if (defaultCard) {
        const { contactLinks } = await getContactLinksByCard(defaultCard._id);
        const visibleLinks = contactLinks ? contactLinks.filter(link => link.isVisible) : [];
        const hasTwoLinks = visibleLinks.length >= 2;
        setAddTwoLinks(hasTwoLinks);
        return hasTwoLinks;
      }
      return false;
    } catch (error) {
      console.error('Error checking contact links status:', error);
      return false;
    }
  };

  // Method to check if user has captured at least one lead
  const checkLeadCapture = async (userId) => {
    try {
      // Check if we're in signup flow - if so, skip this check
      const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
      if (isSignupFlowActive) {
        console.log('🔍 useProgressTracker: Skipping checkLeadCapture during signup flow');
        return false;
      }

      const leads = await getLeadsByUser(userId);
      const hasLeads = leads && leads.length > 0;
      setCaptureLead(hasLeads);
      return hasLeads;
    } catch (error) {
      console.error('Error checking lead capture status:', error);
      return false;
    }
  };

  // Method to check if card has been shared
  const checkCardShare = async (userId) => {
    try {
      // Check if we're in signup flow - if so, skip this check
      const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
      if (isSignupFlowActive) {
        console.log('🔍 useProgressTracker: Skipping checkCardShare during signup flow');
        return false;
      }

      const defaultCard = await getDefaultCardByUserId(userId);
      if (defaultCard) {
        const hasShares = defaultCard.shareCount > 0;
        setShareCard(hasShares);
        return hasShares;
      }
      return false;
    } catch (error) {
      console.error('Error checking card share status:', error);
      return false;
    }
  };

  return {
    createCard,
    addTwoLinks,
    addProfilePhoto,
    shareCard,
    captureLead,
    completedSteps,
    setCreateCard,
    setAddTwoLinks,
    setAddProfilePhoto,
    setShareCard,
    setCaptureLead,
    checkCardCreation,
    checkProfilePhoto,
    checkTwoLinks,
    checkLeadCapture,
    checkCardShare,
    updateCompletedSteps,
  };
};

export default useProgressTracker;